import { Plus } from 'lucide-react'
import { useState, type SubmitEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/problemDetails'
import { Alert, Button, toast } from '@/components/ui'
import { useTenants } from '@/features/tenants/queries'

import { useCreateAgent } from '../queries'
import { AgentPersonaFields } from './AgentPersonaFields'

/** Cria um novo agente: botão que revela o formulário de persona (nome + descrição + instruções). */
export function CreateAgentForm({ tenantId }: { readonly tenantId: string }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [systemPrompt, setSystemPrompt] = useState('')
  const mutation = useCreateAgent(tenantId)
  // Nome da organização para os modelos de instruções (lista já em cache; ausente = texto genérico).
  const companyName = useTenants().data?.find((tenant) => tenant.id === tenantId)?.name

  const reset = () => {
    setName('')
    setDescription('')
    setSystemPrompt('')
    mutation.reset()
  }

  const submit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (name.trim().length === 0 || mutation.isPending) {
      return
    }
    mutation.mutate(
      { name: name.trim(), description: description.trim(), systemPrompt: systemPrompt.trim() },
      {
        onSuccess: (agent) => {
          reset()
          setOpen(false)
          toast.success(t('agents.toast.created', { name: agent.name }))
        },
        onError: (error) => {
          toast.error(error instanceof ApiError ? error.problem.title : t('agents.actionFailed'))
        },
      },
    )
  }

  if (!open) {
    return (
      <div className="flex justify-end">
        <Button
          variant="secondary"
          onClick={() => {
            setOpen(true)
          }}
        >
          <Plus aria-hidden="true" className="h-4 w-4" />
          {t('agents.create.cta')}
        </Button>
      </div>
    )
  }

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-4 rounded-lg border border-border-default bg-background-elevated p-5"
    >
      <div>
        <h2 className="text-sm font-semibold text-foreground-primary">
          {t('agents.create.title')}
        </h2>
        <p className="text-sm text-foreground-muted">{t('agents.create.subtitle')}</p>
      </div>

      {mutation.isError ? (
        <Alert variant="danger" title={t('agents.create.errorTitle')}>
          {mutation.error instanceof ApiError
            ? mutation.error.problem.title
            : t('agents.actionFailed')}
        </Alert>
      ) : null}

      <AgentPersonaFields
        name={name}
        description={description}
        systemPrompt={systemPrompt}
        companyName={companyName}
        disabled={mutation.isPending}
        onName={setName}
        onDescription={setDescription}
        onSystemPrompt={setSystemPrompt}
      />

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          disabled={mutation.isPending}
          onClick={() => {
            reset()
            setOpen(false)
          }}
        >
          {t('agents.create.cancel')}
        </Button>
        <Button type="submit" disabled={name.trim().length === 0 || mutation.isPending}>
          {mutation.isPending ? t('agents.create.submitting') : t('agents.create.submit')}
        </Button>
      </div>
    </form>
  )
}
